import html2canvas from "html2canvas";
import jsPDF from "jspdf";

class PrinterHelper {
  /**
   * Print ZPL code via Zebra Browser Print JS API
   */
  static async printZpl(zplCode) {
    return new Promise((resolve, reject) => {
      if (!window.BrowserPrint) {
        console.error("BrowserPrint library not loaded");
        return reject("BrowserPrint library not loaded");
      }

      window.BrowserPrint.getDefaultDevice(
        "printer",
        function (printer) {
          if (!printer) {
            console.error("Geen printer gevonden");
            return reject("Geen printer gevonden");
          }

          printer.send(
            zplCode,
            function () {
              console.log("Print succesvol!");
              resolve(true);
            },
            function (error) {
              console.error("Print fout:", error);
              reject(error);
            },
          );
        },
        function (error) {
          console.error("Fout bij ophalen printer:", error);
          reject(error);
        },
      );
    });
  }

  /**
   * Convert HTML to ZPL image
   * @param {HTMLElement} html
   * @returns {Promise<string>}
   */
  static async getZplImageFromHtml(html) {
    await PrinterHelper.waitForImagesToLoad(html);

    const canvas = await html2canvas(html, {
      backgroundColor: "#FFFFFF",
      scale: 1,
      useCORS: true,
    });

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Convert to grayscale 2D matrix
    const gray = [];
    for (let y = 0; y < height; y++) {
      gray[y] = [];
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const val = 0.299 * r + 0.587 * g + 0.114 * b;
        gray[y][x] = val;
      }
    }

    // Apply Floyd–Steinberg dithering
    const bw = new Array(width * height).fill(0);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const oldPixel = gray[y][x];
        const newPixel = oldPixel < 128 ? 0 : 255;
        const error = oldPixel - newPixel;
        gray[y][x] = newPixel;
        bw[y * width + x] = newPixel === 0 ? 1 : 0;

        if (x + 1 < width) gray[y][x + 1] += (error * 7) / 16;
        if (x - 1 >= 0 && y + 1 < height)
          gray[y + 1][x - 1] += (error * 3) / 16;
        if (y + 1 < height) gray[y + 1][x] += (error * 5) / 16;
        if (x + 1 < width && y + 1 < height)
          gray[y + 1][x + 1] += (error * 1) / 16;
      }
    }

    // Convert to hex ZPL
    const bytesPerRow = Math.ceil(width / 8);
    const totalBytes = bytesPerRow * height;
    const hexLines = [];

    for (let y = 0; y < height; y++) {
      for (let byte = 0; byte < bytesPerRow; byte++) {
        let value = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = byte * 8 + bit;
          if (x < width && bw[y * width + x]) {
            value |= 1 << (7 - bit);
          }
        }
        hexLines.push(value.toString(16).padStart(2, "0").toUpperCase());
      }
    }

    const hexData = hexLines.join("");
    const zpl = `^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}`;
    return zpl;
  }

  static async getZplFromHtml(html, labelData = {}) {
    if (!html) return "";

    // Label
    if (html.id.startsWith("[PRINT_LABEL_READONLY]")) {
      //Determine the position and size of the label
      const rect = html.getBoundingClientRect();
      const x = Math.round(rect.left);
      const y = Math.round(rect.top);
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);

      //Create the label ZPL code
      let zpl = "^XA\n";
      for (const item of html.children) {
        const itemZpl = await PrinterHelper.getZplFromHtml(item, {
          x,
          y,
          width,
          height,
        });
        zpl += itemZpl;
      }
      zpl += "^XZ";
      return zpl;
    }

    // Label item
    if (html.id.startsWith("[PRINT_LABEL_ITEM]")) {
      //Get the first child of the label item
      const child = html.children[0];
      if (!child) return "";

      //Determine the position and size of the item
      const rect = html.getBoundingClientRect();
      const x = Math.round(rect.left) - labelData.x;
      const y = Math.round(rect.top) - labelData.y;
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);

      const tag = child.tagName.toLowerCase();

      //Handle text elements
      if (tag === "p") {
        const childStyle = window.getComputedStyle(child);

        //Determine the text properties
        const fontSize = parseInt(childStyle.fontSize || "12", 10);
        const zplFontSize = Math.round(fontSize);

        const fontWeight = childStyle.fontWeight || "normal";
        const isBold = fontWeight === "bold" || fontWeight >= 700;

        const textAlign = childStyle.textAlign || "left";
        const zplTextAlign =
          textAlign === "center" ? "C" : textAlign === "right" ? "R" : "L";

        const text = child.textContent?.trim() || "";

        // Create ZPL text command
        const getZplText = (x, y) => {
          return (
            `^CF0,${zplFontSize}` +
            `^FO${x},${y}` +
            `^FB${width},5,0,${zplTextAlign},0` +
            `^FD${text}^FS\n`
          );
        };

        // Generate ZPL text code
        let zplText = getZplText(x, y);
        if (isBold) {
          zplText += getZplText(x + 1, y + 1);
          zplText += getZplText(x + 2, y + 2);
        }
        return zplText;
      }

      // Handle images and canvas
      if (tag === "img" || tag === "canvas") {
        const imageZpl = await PrinterHelper.getZplImageFromHtml(child);
        return `^FO${x},${y}${imageZpl}^FS\n`;
      }

      //Handle boxes
      if (tag === "div") {
        return `^FO${x},${y}^GB${width},${height},${Math.min(
          width,
          height,
        )},B^FS\n`;
      }

      return "";
    }

    return "";
  }

  /**
   * Wait for all images in the given HTML element to load
   * @param {HTMLElement} html
   * @returns {Promise<void>}
   */
  static waitForImagesToLoad(html) {
    const images = html.querySelectorAll("img");
    const promises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      });
    });
    return Promise.all(promises);
  }

  /**
   * This method will convert the given value from mm to px based on the DPI.
   * @param {number} value
   * @param {number} dpi
   * @param {boolean} positive
   * @returns {number}
   */
  static getMmToPx(value, dpi, positive = true) {
    return positive ? value * (dpi / 25.4) : value / (dpi / 25.4);
  }

  /**
   * Convert points to pixels
   * @param {number} value
   * @param {number} dpi
   * @returns {number}
   */
  static getMmToPt(value, dpi) {
    return value * (dpi / 72);
  }

  /**
   * This method will generate a PDF from the given HTML element and return it as a Blob.
   * @param {HTMLElement} html
   * @param {number} width
   * @param {number} height
   * @returns {Promise<Blob>}
   */
  static async getPdfFromHtml(html, width, height, rotate = false) {
    await PrinterHelper.waitForImagesToLoad(html);

    const canvas = await html2canvas(html, {
      backgroundColor: "#FFFFFF",
      useCORS: true,
    });

    let outputCanvas = canvas;
    let pdfWidth = width;
    let pdfHeight = height;

    if (rotate) {
      // Canvas 90 graden draaien
      const rotatedCanvas = document.createElement("canvas");
      const ctx = rotatedCanvas.getContext("2d");

      rotatedCanvas.width = canvas.height;
      rotatedCanvas.height = canvas.width;

      ctx.translate(rotatedCanvas.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(canvas, 0, 0);

      outputCanvas = rotatedCanvas;

      // PDF-afmetingen ook omwisselen
      pdfWidth = height;
      pdfHeight = width;
    }

    const image = outputCanvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
      unit: "mm",
      format: [pdfWidth, pdfHeight],
    });

    pdf.addImage(image, "PNG", 0, 0, pdfWidth, pdfHeight);

    return {
      output: pdf.output("blob"),
      pdfWidth,
      pdfHeight,
    };
  }
}

export default PrinterHelper;
